import { useEffect, useRef, useState } from "react";
import { useProcessSteps } from "@/hooks/useSiteContent";
import ContentStatus from "@/components/feature/ContentStatus";

export default function ProcessSection() {
  const { data: steps, loading, error, reload } = useProcessSteps();
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-100 text-primary-700 text-xs md:text-sm font-label font-medium rounded-full mb-4">
            <i className="ri-route-line" />
            Hành trình trải nghiệm
          </span>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Quy trình chăm sóc đơn giản
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto font-body">
            Chỉ 4 bước đơn giản để bạn bắt đầu hành trình chăm sóc sức khỏe cùng Xanh Wellness.
          </p>
        </div>

        <ContentStatus loading={loading} error={error} onRetry={reload}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`relative bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6 transition-all duration-500 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: visible ? `${index * 120}ms` : "0ms" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 flex-shrink-0">
                    <i className={`${step.icon} text-xl md:text-2xl`} />
                  </div>
                  <span className="font-display text-3xl md:text-4xl font-bold text-foreground-200">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display text-base md:text-lg font-semibold text-foreground-950 mb-2">
                  {step.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed font-body">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </ContentStatus>
      </div>
    </section>
  );
}